import { useEffect, useMemo, useState } from "react";
import {
  Stack, Card, Group, Button, Text, Badge, Divider, Loader, Center,
  SegmentedControl, Modal, Alert, List, ActionIcon,
} from "@mantine/core";
import {
  IconCheck, IconX, IconUser, IconAlertTriangle,
  IconMinus, IconPlus, IconTrash, IconRestore,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader, EmptyState } from "../../../shared/ui";

/** Kuruş → "₺240,00" — birim fiyat satırları için. */
function formatTRY(minor: number | null | undefined): string {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
  }).format((minor || 0) / 100);
}
import {
  useQrPendingOrders, useApproveQrOrder, useRejectQrOrder,
  useQrPendingRequests, useApproveQrRequest, useRejectQrRequest,
} from "../../../shared/api";
import { useWebSocket } from "../../../shared/ws";
import { useAuth } from "../../../shared/auth/AuthProvider";

export function QrApprovalPage() {
  // Eski mimari (DRAFT Order'lar) — backward compat için sürdürülüyor.
  const approveLegacy = useApproveQrOrder();
  const rejectLegacy = useRejectQrOrder();
  // Yeni mimari — PendingQrRequest (Order yaratılmadan müşteri isteği)
  const approveReq = useApproveQrRequest();
  const rejectReq = useRejectQrRequest();
  const qc = useQueryClient();
  const { lastMessage, status: wsStatus } = useWebSocket<any>("/ws/waiters/");
  const { data, isLoading } = useQrPendingOrders({ wsConnected: wsStatus === "OPEN" });
  const { data: reqData, isLoading: reqLoading } = useQrPendingRequests({
    wsConnected: wsStatus === "OPEN",
  });
  const { user } = useAuth();
  const currentUserId = user?.id || null;
  // Filtre: "mine" = sadece kendi masalarım (varsayılan), "all" = tümü
  const [scope, setScope] = useState<"mine" | "all">("mine");
  // Stok yetersizlik pop-up'ı — approve sırasında shortages payload alınca açılır.
  const [shortageState, setShortageState] = useState<{
    requestId: string;
    shortages: any[];
    detail: string;
  } | null>(null);

  // Garson masada miktar düzeltmesi (örn. "fazla gelmiş, azalt"). Onay
  // ANINDA backend'e line_overrides olarak gider; stok daha düşmeden uygulanır.
  // Şekil: { [requestId]: { [lineIdx]: yeniMiktar } }. Kayıt yoksa snapshot
  // miktarı geçerli; 0 → kalem tamamen çıkar.
  const [lineOverrides, setLineOverrides] = useState<
    Record<string, Record<number, number>>
  >({});

  /** Bir isteğin bir satırının geçerli (override veya orijinal) miktarı. */
  function effQty(o: any, idx: number): number {
    const ov = lineOverrides[o.id]?.[idx];
    return ov === undefined ? (o.lines[idx]?.quantity ?? 0) : ov;
  }

  /** Satır miktarını delta kadar değiştir (0'ın altına inmez). */
  function bumpQty(o: any, idx: number, delta: number) {
    setLineOverrides((prev) => {
      const cur = effQty(o, idx);
      const next = Math.max(0, cur + delta);
      return { ...prev, [o.id]: { ...(prev[o.id] || {}), [idx]: next } };
    });
  }

  /** Bu istek için garson herhangi bir satırı düzenledi mi? */
  function isEdited(o: any): boolean {
    const ovs = lineOverrides[o.id];
    if (!ovs) return false;
    return Object.entries(ovs).some(
      ([idx, q]) => q !== (o.lines[Number(idx)]?.quantity ?? 0),
    );
  }

  /** Düzenlenmiş satır toplamı (₺) — kart başlığındaki toplamı tazelemek için. */
  function editedTotal(o: any): number {
    return (o.lines || []).reduce((sum: number, l: any, idx: number) => {
      const q = effQty(o, idx);
      const unit = (l.unit_price_minor || 0) +
        (Array.isArray(l.modifiers)
          ? l.modifiers.reduce((s: number, m: any) => s + (m.price_minor || 0), 0)
          : 0);
      return sum + q * unit;
    }, 0);
  }

  /** approve çağrısına geçilecek line_overrides (sadece değişenler). */
  function overridesPayload(o: any): Record<string, number> | undefined {
    const ovs = lineOverrides[o.id];
    if (!ovs) return undefined;
    const payload: Record<string, number> = {};
    for (const [idx, q] of Object.entries(ovs)) {
      if (q !== (o.lines[Number(idx)]?.quantity ?? 0)) payload[idx] = q;
    }
    return Object.keys(payload).length ? payload : undefined;
  }

  useEffect(() => {
    if (!lastMessage) return;
    const ev = lastMessage.event;

    // Yeni QR siparişi geldi — sadece onay listesini tazele. Toast'ı
    // burada gösterME: App.tsx (global) seviyesinde gösteriliyor, böylece
    // garson hangi sayfada olursa olsun bildirimi alır. Burada
    // tekrarlamak duplicate yaratır.
    if (ev === "qr.order.pending_approval") {
      qc.invalidateQueries({ queryKey: ["qr-pending"] });
    }
    // Yeni mimari — PendingQrRequest event'leri. cancelled DAHİL: müşteri
    // garson onayı beklerken vazgeçerse istek listeden anında düşmeli;
    // yoksa garson ekranında "onay bekliyor" olarak takılı kalır.
    //
    // refetchType: "all" KRİTİK: bu sayfa WS bağlıyken refetchInterval=false
    // olduğu için query "background"da sayılabiliyor; düz invalidate stale
    // işaretliyor ama hemen refetch ETMİYOR — kart ancak sayfa değişip
    // remount olunca düşüyordu ("başka sayfaya geçince düzeliyor" bug'ı).
    // "all" aktif + inaktif tüm eşleşen query'leri zorla yeniden çeker.
    if (
      ev === "qr.request.pending_approval" ||
      ev === "qr.request.approved" ||
      ev === "qr.request.rejected" ||
      ev === "qr.request.cancelled"
    ) {
      qc.invalidateQueries({
        queryKey: ["qr-requests-pending"],
        refetchType: "all",
      });
    }

    // kds.ticket.ready toast'ı App.tsx (global) seviyesinde gösterilir —
    // burada tekrarlamak duplicate yaratır.

    // Müşteri QR'dan hesap/ödeme istedi — garsona masaya gitme bildirimi
    if (ev === "qr.payment.requested" || ev === "qr.payment.call_waiter") {
      const splitMode = lastMessage.split_mode;
      const parts = lastMessage.split_parts;
      const splitNote =
        splitMode === "split" && parts > 1
          ? ` — ${parts} parça (kişi başı yaklaşık ${
              ((lastMessage.per_part_minor || 0) / 100).toFixed(2)
            } ₺)`
          : "";
      notifications.show({
        title: "💳 Ödeme isteği",
        message: `${lastMessage.table_name || "Masa"} hesabı istiyor${splitNote}`,
        color: "blue",
        autoClose: false, // garson görene kadar kalsın
      });
      qc.invalidateQueries({ queryKey: ["orders"] });
    }
  }, [lastMessage, qc]);

  // WS (yeniden) bağlandığında listeyi tazele. Dev'de runserver auto-reload
  // ara ara WS'i koparır (ERR_CONNECTION_REFUSED); kopuk dönemde gelen
  // pending/cancelled event'leri kaçar. Reconnect'te bir kez fresh çekerek
  // kaçanı yakalarız — aksi halde garson ekranı eski listede takılı kalır.
  useEffect(() => {
    if (wsStatus === "OPEN") {
      qc.invalidateQueries({
        queryKey: ["qr-requests-pending"], refetchType: "all",
      });
      qc.invalidateQueries({ queryKey: ["qr-pending"], refetchType: "all" });
    }
  }, [wsStatus, qc]);

  // Eski DRAFT siparişler (backward compat) + Yeni PendingQrRequest'ler
  const legacyList: any[] = (data?.results || data || []).map((o: any) => ({
    ...o, _kind: "legacy" as const,
  }));
  const requestList: any[] = (reqData?.results || reqData || []).map((r: any) => ({
    ...r, _kind: "request" as const,
  }));
  const allOrders: any[] = [...requestList, ...legacyList];

  /** Bu sipariş giriş yapan garsona GERÇEKTEN atanmış bir masada mı?
   *
   * "Masalarım" = yalnız bana atanmış masalar. Atanmamış (sahipsiz) masa
   * buraya GİRMEZ — adamın masası değil; "Tümü" sekmesinde görünür ve
   * oradaki sayıya dahildir, böylece garson yine de görüp onaylayabilir. */
  function isMyTable(o: any): boolean {
    return !!currentUserId && o.table_assigned_waiter === currentUserId;
  }

  // "Kendi masalarım" filtresi — kaç sipariş başkasının masasında?
  const mineCount = useMemo(
    () => allOrders.filter(isMyTable).length,
    [allOrders, currentUserId],
  );
  const otherCount = allOrders.length - mineCount;

  const orders =
    scope === "mine" ? allOrders.filter(isMyTable) : allOrders;

  // Aynı masanın bekleyen isteklerini grupla — garson tek kart altında görüp
  // "Tümünü Onayla" ile toplu onaylayabilsin (mutfak tek hesapta toplanır).
  // İstekler DB'de ayrı kalır; gruplama yalnız görsel + toplu eylem içindir.
  // Grup anahtarı table_id (yoksa table_name); ekleme sırası korunur, böylece
  // istekler geliş sırasına göre (eskiden yeniye) listelenir.
  const tableGroups = useMemo(() => {
    const map = new Map<string, { key: string; tableName: string; items: any[] }>();
    for (const o of orders) {
      const key = String(o.table ?? o.table_id ?? o.table_name ?? o.id);
      if (!map.has(key)) {
        map.set(key, { key, tableName: o.table_name || "Masa", items: [] });
      }
      map.get(key)!.items.push(o);
    }
    return Array.from(map.values());
  }, [orders]);

  if (isLoading || reqLoading) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }

  /** Tek isteği onayla. Başarı → true. Stok yetersizliği/hata → false
   *  (toplu onayda zincirin durması için). Bildirimi `notify` true iken gösterir
   *  — toplu onayda her istek için ayrı toast yerine sonda tek özet gösterilir. */
  async function approveOne(item: any, { notify = true }: { notify?: boolean } = {}): Promise<boolean> {
    if (item._kind === "request") {
      try {
        const overrides = overridesPayload(item);
        await approveReq.mutateAsync({
          requestId: item.id,
          ...(overrides ? { line_overrides: overrides } : {}),
        });
        if (notify) {
          notifications.show({
            message: "Sipariş onaylandı, mutfağa gönderildi",
            color: "green",
          });
        }
        return true;
      } catch (e: any) {
        const resp = e?.response?.data;
        // Stok yetersizliği — pop-up aç, garson is_optional malzemeleri
        // çıkarıp retry yapsın.
        if (resp?.shortages && Array.isArray(resp.shortages)) {
          setShortageState({
            requestId: item.id,
            shortages: resp.shortages,
            detail: resp.detail || "Stok yetersiz",
          });
          return false;
        }
        // 409 = istek artık PENDING değil (müşteri iptal etti / başka garson
        // çözdü). Listeyi tazele ki "hayalet" kart düşsün.
        if (e?.response?.status === 409) {
          qc.invalidateQueries({ queryKey: ["qr-requests-pending"] });
        }
        notifications.show({
          message: resp?.detail || "Onay başarısız",
          color: "red",
        });
        return false;
      }
    } else {
      try {
        await approveLegacy.mutateAsync(item.id);
        if (notify) {
          notifications.show({
            message: "Sipariş onaylandı, mutfağa gönderildi",
            color: "green",
          });
        }
        return true;
      } catch (e: any) {
        notifications.show({
          message: e?.response?.data?.detail || "Onay başarısız",
          color: "red",
        });
        return false;
      }
    }
  }

  async function handleApprove(item: any) {
    await approveOne(item);
  }

  /** Bir masadaki tüm bekleyen istekleri sırayla onayla. İlk takılan istekte
   *  (stok yetersizliği/hata) durur — garson o isteği çözüp tekrar deneyebilir;
   *  kalanlar listede PENDING kalır. Tek tek backend approve çağrılır:
   *  idempotency, stok düşüşü ve red/iptal akışı istek-bazlı korunur. */
  async function handleApproveAll(items: any[]) {
    let ok = 0;
    for (const it of items) {
      const success = await approveOne(it, { notify: false });
      if (!success) break; // shortage modal'ı açıldıysa veya hata → dur
      ok++;
    }
    if (ok > 0) {
      notifications.show({
        message:
          ok === items.length
            ? `${ok} sipariş onaylandı, mutfağa gönderildi`
            : `${ok}/${items.length} sipariş onaylandı — kalanı kontrol edin`,
        color: ok === items.length ? "green" : "orange",
      });
    }
  }

  async function handleReject(item: any) {
    if (item._kind === "request") {
      try {
        await rejectReq.mutateAsync({
          requestId: item.id, reason: "Garson reddetti",
        });
        notifications.show({ message: "Sipariş reddedildi", color: "orange" });
      } catch (e: any) {
        notifications.show({
          message: e?.response?.data?.detail || "Red başarısız",
          color: "red",
        });
      }
    } else {
      try {
        await rejectLegacy.mutateAsync({
          orderId: item.id, reason: "Garson reddetti",
        });
        notifications.show({ message: "Sipariş reddedildi", color: "orange" });
      } catch (e: any) {
        notifications.show({
          message: e?.response?.data?.detail || "Red başarısız",
          color: "red",
        });
      }
    }
  }

  /** Tek isteğin kalem listesi (ürün + modifier + not). Hem tekil kartta hem
   *  grup içindeki alt-bloklarda kullanılır. Yeni mimari (PendingQrRequest)
   *  satırlarında garson miktarı masada düzeltebilir (−/+/kaldır). */
  function renderLines(o: any) {
    const editable = o._kind === "request";
    return (
      <Stack gap={4}>
        {(o.lines || []).map((l: any, i: number) => {
          const qty = editable ? effQty(o, i) : l.quantity;
          const removed = editable && qty === 0;
          const changed = editable && qty !== l.quantity;
          return (
          <Stack key={i} gap={0}>
            <Group justify="space-between" wrap="nowrap" gap="xs" align="center">
              <Group gap={6} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                {editable && (
                  <Group gap={2} wrap="nowrap" style={{ flexShrink: 0 }}>
                    <ActionIcon
                      size="sm" variant="light" color="gray"
                      onClick={() => bumpQty(o, i, -1)}
                      disabled={qty === 0}
                      aria-label="Azalt"
                    >
                      <IconMinus size={14} />
                    </ActionIcon>
                    <Text size="sm" fw={700} w={20} ta="center">
                      {qty}
                    </Text>
                    <ActionIcon
                      size="sm" variant="light" color="gray"
                      onClick={() => bumpQty(o, i, 1)}
                      aria-label="Artır"
                    >
                      <IconPlus size={14} />
                    </ActionIcon>
                  </Group>
                )}
                <Text
                  size="sm"
                  lineClamp={1}
                  style={{
                    flex: 1, minWidth: 0,
                    textDecoration: removed ? "line-through" : undefined,
                  }}
                  {...(removed ? { c: "dimmed" } : {})}
                >
                  {editable ? "" : `• ${l.quantity}× `}{l.product_name}
                </Text>
                {editable && (
                  <ActionIcon
                    size="sm" variant="subtle"
                    color={removed ? "blue" : "red"}
                    onClick={() => bumpQty(o, i, removed ? l.quantity : -qty)}
                    aria-label={removed ? "Geri al" : "Kaldır"}
                  >
                    {removed ? <IconRestore size={14} /> : <IconTrash size={14} />}
                  </ActionIcon>
                )}
              </Group>
              <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                {/* Birim fiyat — adet × kaç ₺ */}
                <Text size="xs" c="dimmed">
                  {qty} × {formatTRY(l.unit_price_minor)}
                </Text>
                <Text size="sm" fw={600} {...(changed ? { c: "blue" } : {})}>
                  {formatTRY(
                    editable
                      ? qty * l.unit_price_minor
                      : (l.line_total_minor ?? l.unit_price_minor * l.quantity),
                  )}
                </Text>
              </Group>
            </Group>
            {/* Modifier'lar — mutfak/garson görsün + ücretli olanın fiyatı.
                Yeni kayıtlar yapısal `modifiers` ({name, price_minor}) taşır;
                eski kayıtlar yalnız `modifier_names` (fiyatsız) taşır. */}
            {Array.isArray(l.modifiers) && l.modifiers.length > 0 ? (
              <Text size="xs" c="dimmed" pl="md">
                {l.modifiers
                  .map((m: any) =>
                    m.price_minor
                      ? `+ ${m.name} (${formatTRY(m.price_minor)})`
                      : `+ ${m.name}`,
                  )
                  .join(", ")}
              </Text>
            ) : Array.isArray(l.modifier_names) && l.modifier_names.length > 0 ? (
              <Text size="xs" c="dimmed" pl="md">
                {l.modifier_names.map((m: string) => `+ ${m}`).join(", ")}
              </Text>
            ) : null}
            {l.notes && (
              <Text size="xs" c="dimmed" fs="italic" pl="md">
                “{l.notes}”
              </Text>
            )}
          </Stack>
          );
        })}
      </Stack>
    );
  }

  /** Tek isteğin Reddet/Onayla buton çifti. */
  function renderActions(o: any, isReallyMine: boolean) {
    return (
      <Group grow mt="xs">
        <Button
          color="red"
          variant="light"
          leftSection={<IconX size={18} />}
          onClick={() => handleReject(o)}
          loading={rejectLegacy.isPending || rejectReq.isPending}
        >
          Reddet
        </Button>
        <Button
          color="green"
          variant={isReallyMine ? "filled" : "light"}
          leftSection={<IconCheck size={18} />}
          onClick={() => handleApprove(o)}
          loading={approveLegacy.isPending || approveReq.isPending}
        >
          Onayla
        </Button>
      </Group>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader title="QR Onay" subtitle={`${orders.length} sipariş bekliyor`} />

      {/* Kapsam filtresi — varsayılan kendi masalarım, istenirse tümü */}
      <SegmentedControl
        fullWidth
        value={scope}
        onChange={(v) => setScope(v as "mine" | "all")}
        data={[
          { label: `Masalarım (${mineCount})`, value: "mine" },
          { label: `Tümü (${allOrders.length})`, value: "all" },
        ]}
      />

      {orders.length === 0 ? (
        <EmptyState
          title={
            scope === "mine"
              ? "Masalarınıza ait onay bekleyen sipariş yok"
              : "Onay bekleyen QR siparişi yok"
          }
          description={
            scope === "mine" && otherCount > 0
              ? `${otherCount} sipariş başka garsonların masalarında — "Tümü" sekmesinden görebilirsiniz.`
              : "Yeni QR siparişi geldiğinde burada görünecek."
          }
        />
      ) : (
        tableGroups.map((g) => {
          // Grup sahipliği/rengi ilk isteğe göre (aynı masa = aynı sahiplik).
          const head = g.items[0];
          const hasWaiter = !!head.table_assigned_waiter;
          const isReallyMine = isMyTable(head);
          const accent = isReallyMine ? "red" : hasWaiter ? "orange" : "gray";
          // Garson düzenlemesini yansıtan toplam (override varsa onu kullanır).
          const groupTotal = g.items.reduce(
            (s, it) => s + (it._kind === "request" ? editedTotal(it) : (it.total_minor || 0)), 0,
          );
          const multi = g.items.length > 1;

          // Masa sahiplik rozeti (tekil + grup başlığında ortak).
          const ownershipBadge = isReallyMine ? (
            <Badge size="sm" color="red" variant="filled" leftSection={<IconUser size={12} />}>
              Masanız
            </Badge>
          ) : hasWaiter ? (
            <Badge size="sm" color="orange" variant="light" leftSection={<IconUser size={12} />}>
              {head.table_assigned_waiter_name || "Başka garson"}
            </Badge>
          ) : (
            <Badge size="sm" color="gray" variant="light">
              Atanmamış
            </Badge>
          );

          return (
            <Card
              key={g.key}
              padding="md"
              radius="md"
              withBorder
              style={{
                borderColor: `var(--mantine-color-${accent}-5)`,
                borderWidth: isReallyMine ? 2 : 1,
              }}
            >
              <Stack gap="xs">
                <Group justify="space-between">
                  <Group>
                    <Badge size="lg" color={accent}>
                      {g.tableName}
                    </Badge>
                    {ownershipBadge}
                    {multi && (
                      <Badge size="sm" color="blue" variant="light">
                        {g.items.length} sipariş
                      </Badge>
                    )}
                    {!multi && (
                      <Text size="sm" c="dimmed">
                        {new Date(head.created_at).toLocaleTimeString("tr-TR")}
                      </Text>
                    )}
                    {!multi && isEdited(head) && (
                      <Badge size="sm" color="blue" variant="light">
                        düzenlendi
                      </Badge>
                    )}
                  </Group>
                  <Text fw={700} c="blue">
                    {formatTRY(groupTotal)}
                  </Text>
                </Group>

                {/* Başka garsonun masası — yine onaylanabilir ama uyarılır */}
                {!isReallyMine && hasWaiter && (
                  <Text size="xs" c="orange.7">
                    Bu masa {head.table_assigned_waiter_name || "başka bir garsona"} ait — yine de onaylayabilirsiniz.
                  </Text>
                )}

                {multi ? (
                  <>
                    {/* Her bekleyen istek ayrı blok — kendi zamanı, kalemleri,
                        tekil Reddet/Onayla'sıyla. Üstte toplu onay. */}
                    {g.items.map((o: any, idx: number) => (
                      <Card
                        key={o.id}
                        padding="sm"
                        radius="sm"
                        withBorder
                        bg="var(--mantine-color-gray-0)"
                      >
                        <Stack gap={4}>
                          <Group justify="space-between">
                            <Group gap={6} wrap="nowrap">
                              <Text size="xs" c="dimmed" fw={600}>
                                #{idx + 1} · {new Date(o.created_at).toLocaleTimeString("tr-TR")}
                                {o.customer_name ? ` · ${o.customer_name}` : ""}
                              </Text>
                              {isEdited(o) && (
                                <Badge size="xs" color="blue" variant="light">
                                  düzenlendi
                                </Badge>
                              )}
                            </Group>
                            <Text size="sm" fw={600} c="blue">
                              {formatTRY(editedTotal(o))}
                            </Text>
                          </Group>
                          <Divider />
                          {renderLines(o)}
                          {renderActions(o, isReallyMine)}
                        </Stack>
                      </Card>
                    ))}
                    <Button
                      color="green"
                      variant={isReallyMine ? "filled" : "light"}
                      leftSection={<IconCheck size={18} />}
                      onClick={() => handleApproveAll(g.items)}
                      loading={approveLegacy.isPending || approveReq.isPending}
                      mt="xs"
                    >
                      Tümünü Onayla ({g.items.length})
                    </Button>
                  </>
                ) : (
                  <>
                    <Divider />
                    {renderLines(head)}
                    {renderActions(head, isReallyMine)}
                  </>
                )}
              </Stack>
            </Card>
          );
        })
      )}

      {/* Stok yetersizlik pop-up'ı — approve sırasında shortages payload
          gelince açılır. Garson "Opsiyonel malzemeleri çıkar ve onayla"
          basarak retry yapar; backend extra_excluded_ingredient_ids ile
          eksik kalemler hariç tekrar dener. Stoğa kaynama dahil olduğu için
          opsiyonel olmayan malzeme yetersizse retry mümkün değil — garson
          "Reddet" basar. */}
      <Modal
        opened={!!shortageState}
        onClose={() => setShortageState(null)}
        title="Stok yetersiz"
        centered
        zIndex={400}
      >
        {shortageState && (
          <Stack gap="md">
            <Alert
              color="orange"
              icon={<IconAlertTriangle size={18} />}
            >
              {shortageState.detail}
            </Alert>
            <Text size="sm" fw={600}>Eksik malzemeler:</Text>
            <List spacing="xs" size="sm">
              {shortageState.shortages.map((sh: any, i: number) => (
                <List.Item key={i}>
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm">
                      <strong>{sh.name}</strong> — gerek {sh.need} {sh.unit},
                      mevcut {sh.have} {sh.unit}
                    </Text>
                    {sh.is_optional && (
                      <Badge size="xs" color="green" variant="light">
                        opsiyonel
                      </Badge>
                    )}
                  </Group>
                </List.Item>
              ))}
            </List>
            {/* Tüm eksiklikler is_optional ise garson "Hepsini çıkar ve onayla"
                basabilir. Aksi halde sadece reddetmeli. */}
            {shortageState.shortages.every((s: any) => s.is_optional) ? (
              <Button
                color="green"
                leftSection={<IconCheck size={16} />}
                loading={approveReq.isPending}
                onClick={async () => {
                  // Bu basit MVP: tüm opsiyonel eksiklikleri ilk satıra
                  // ata. Bu MVP yaklaşımı çoklu satırı doğru ayırt etmez,
                  // ancak tek satırlı çoğu sipariş için yeterli. İleride
                  // satır-bazlı UI eklenecek.
                  const allIds = shortageState.shortages.map(
                    (s: any) => s.ingredient_id,
                  );
                  try {
                    await approveReq.mutateAsync({
                      requestId: shortageState.requestId,
                      extra_excluded_ingredient_ids: { "0": allIds },
                    });
                    notifications.show({
                      message: "Sipariş onaylandı (opsiyonel malzemeler çıkarıldı)",
                      color: "green",
                    });
                    setShortageState(null);
                  } catch (e: any) {
                    notifications.show({
                      message:
                        e?.response?.data?.detail ||
                        "Tekrar deneme başarısız",
                      color: "red",
                    });
                  }
                }}
              >
                Opsiyonel malzemeleri çıkar ve onayla
              </Button>
            ) : (
              <Alert color="red" icon={<IconAlertTriangle size={18} />}>
                Bazı zorunlu malzemeler eksik — sipariş onaylanamaz.
                Müşteri ile konuşup reddedebilirsiniz.
              </Alert>
            )}
            <Group justify="flex-end" gap="xs">
              <Button
                variant="default"
                onClick={() => setShortageState(null)}
              >
                Vazgeç
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
